import { concat, fn } from '@ember/helper';
import { HStack, VStack, Section } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { and, or } from 'tio-ui/utilities';
import { PhArrowSquareOut, PhPencil, PhPlus, PhTrash } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';

import * as v from 'valibot';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import Button from 'tio-reimagined/components/button';
import Checkbox from 'tio-reimagined/components/checkbox';
import Component from '@glimmer/component';
import Divider from 'tio-reimagined/components/divider';
import Form from 'tio-reimagined/components/form';
import Input from 'tio-reimagined/components/input';
import inputmask from 'tio-common/modifiers/inputmask';
import Modal from 'tio-reimagined/components/modal';
import ReSelect from 'tio-reimagined/components/select';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import { stateOptions } from 'tio-common/components/strategy-finder/strategy-finder-form';
import Text from 'tio-reimagined/components/text';
import {
  findOverlappingPeriods,
  type EmploymentPeriod,
  type OverlapResult,
} from 'tio-common/utils/pslf-date-overlap';
import type { IntlService } from 'ember-intl';
import type Employee from 'tio-data/models/employee';
import type PslfFormModel from 'tio-data/models/pslf-form';
import type PslfGroup from 'tio-data/models/pslf-group';
import type StoreService from 'ember-data/store';

const EXCLUDED_STATUSES = ['cancelled', 'rejected', 'abandoned', 'expired'];

const MAX_PAST_PERIODS = 6;

const manualEmployerSchema = v.object({
  employerName: v.pipe(
    v.string('Employer legal name is required'),
    v.minLength(1, 'Employer legal name is required')
  ),
  employerFein: v.pipe(
    v.string('FEIN is required'),
    v.minLength(1, 'FEIN is required'),
    v.regex(/^\d{2}-\d{7}$/, 'FEIN must be in format XX-XXXXXXX')
  ),
  employerStreetAddress: v.pipe(
    v.string('Street address is required'),
    v.minLength(1, 'Street address is required')
  ),
  employerCity: v.pipe(v.string('City is required'), v.minLength(1, 'City is required')),
  employerState: v.pipe(v.string('State is required'), v.minLength(1, 'State is required')),
  employerPostalCode: v.pipe(
    v.string('Postal code is required'),
    v.minLength(1, 'Postal code is required'),
    v.regex(/^\d{5}$/, 'Postal code must be 5 digits')
  ),
});

export interface PastEmploymentPeriod {
  id: string;
  pslfGroup?: PslfGroup;
  useManualEntry?: boolean;
  manualEmployerName?: string;
  manualEmployerFein?: string;
  manualEmployerStreetAddress?: string;
  manualEmployerCity?: string;
  manualEmployerState?: string;
  manualEmployerPostalCode?: string;
  employmentStartDate: string;
  employmentEndDate: string;
  employmentHours: number;
}

type ManualEmployerData = {
  employerName: string;
  employerFein: string;
  employerStreetAddress: string;
  employerCity: string;
  employerState: string;
  employerPostalCode: string;
};

type ModalFormData = {
  employmentStartDate: string;
  employmentEndDate: string;
  employmentHours: number;
};

const DEFAULT_MANUAL_EMPLOYER_DATA: ManualEmployerData = {
  employerName: '',
  employerFein: '',
  employerStreetAddress: '',
  employerCity: '',
  employerState: '',
  employerPostalCode: '',
};

const modalFormSchema = v.pipe(
  v.object({
    employmentStartDate: v.pipe(
      v.string('Start date is required'),
      v.minLength(1, 'Start date is required')
    ),
    employmentEndDate: v.pipe(
      v.string('End date is required'),
      v.minLength(1, 'End date is required')
    ),
    employmentHours: v.pipe(
      v.number('Hours per week is required'),
      v.minValue(1, 'Hours must be at least 1'),
      v.maxValue(168, 'Hours cannot exceed 168 per week')
    ),
  }),
  v.forward(
    v.partialCheck(
      [['employmentStartDate'], ['employmentEndDate']],
      (input) => {
        if (!input.employmentStartDate || !input.employmentEndDate) return true;
        return input.employmentEndDate >= input.employmentStartDate;
      },
      'End date must be after start date'
    ),
    ['employmentEndDate']
  )
);

export interface ApproverOboPastPeriodFormSignature {
  Args: {
    sourceForm: PslfFormModel;
    approverEmployee: Employee;
    pastPeriods: PastEmploymentPeriod[];
    onPeriodsChange: (periods: PastEmploymentPeriod[]) => void;
    employeeName?: string;
  };
  Element: HTMLElement;
}

export default class ApproverOboPastPeriodForm extends Component<ApproverOboPastPeriodFormSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  @tracked isOpenPastPeriodModal = false;
  @tracked editingPeriodId: string | null = null;
  @tracked editingPeriodGroup?: PslfGroup;
  @tracked editingPeriodGroupError: string[] = [];
  @tracked useManualEntry = false;
  @tracked manualEmployerData: ManualEmployerData = { ...DEFAULT_MANUAL_EMPLOYER_DATA };
  @tracked manualEmployerErrors: Record<string, string[]> = {};
  @tracked modalFormData: ModalFormData = {
    employmentStartDate: '',
    employmentEndDate: '',
    employmentHours: 0,
  };
  @tracked modalFormErrors: Record<string, string[]> = {};
  @tracked existingPeriods: EmploymentPeriod[] = [];
  @tracked overlapResults: OverlapResult[] = [];

  get borrowerName(): string {
    if (this.args.employeeName) return this.args.employeeName;
    const fields = this.args.sourceForm.borrowerFields;
    return (
      (fields?.['borrower-name'] as string) || this.intl.t('pslf.approver_obo.unknown_employee')
    );
  }

  get groupOptions() {
    return this.args.approverEmployee.company.pslfGroups.map((group: PslfGroup) => ({
      ...group,
      item: group,
      id: group.id,
      label: `${group.employerLegalName} - ${group.employerFein}`,
    }));
  }

  get usStateOptions() {
    return stateOptions.map((state) => ({
      id: state.abbr,
      label: state.name,
    }));
  }

  get canAddMorePeriods() {
    return this.args.pastPeriods.length < MAX_PAST_PERIODS;
  }

  get todayDateString() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get isEditingExistingPeriod() {
    return (
      !!this.editingPeriodId && this.args.pastPeriods.some((p) => p.id === this.editingPeriodId)
    );
  }

  // --- Overlap detection ---

  fetchExistingForms = async () => {
    const employeeId = this.args.sourceForm.employee?.id;
    if (!employeeId) return;

    try {
      const forms = await this.store.query<PslfFormModel>('pslf-form', {
        filter: { 'employee-id': employeeId },
        page: { limit: 50, offset: 0 },
      });

      this.existingPeriods = forms
        .filter((f: PslfFormModel) => !EXCLUDED_STATUSES.includes(f.status))
        .map((f: PslfFormModel) => ({
          startDate:
            f.employerFields?.['employment-start-date'] ||
            f.borrowerFields?.['borrower-employment-start-date'] ||
            '',
          endDate:
            f.employerFields?.['employment-end-date'] ||
            f.borrowerFields?.['borrower-employment-end-date'] ||
            '',
          employerName:
            f.borrowerFields?.['borrower-employer-name'] ||
            f.employerFields?.['employer-name'] ||
            '',
          formId: f.id,
          needsReview: f.needsReview,
          status: f.status,
        }))
        .filter((p: EmploymentPeriod) => p.startDate);
      this.checkForOverlaps();
    } catch {
      // If the fetch fails we still allow the user to proceed without warnings
      this.existingPeriods = [];
    }
  };

  checkForOverlaps = () => {
    const { employmentStartDate, employmentEndDate } = this.modalFormData;
    if (!employmentStartDate || !employmentEndDate) {
      this.overlapResults = [];
      return;
    }

    const candidate: EmploymentPeriod = {
      startDate: employmentStartDate,
      endDate: employmentEndDate || undefined,
    };

    // Build list of periods to check against:
    // 1. Other local past periods (excluding the one being edited)
    const localPeriods: EmploymentPeriod[] = this.args.pastPeriods
      .filter((p) => p.id !== this.editingPeriodId)
      .map((p) => ({
        startDate: p.employmentStartDate,
        endDate: p.employmentEndDate,
        employerName: p.useManualEntry
          ? p.manualEmployerName
          : p.pslfGroup?.employerLegalName || '',
      }));

    // 2. Existing forms from the API
    const allPeriods = [...localPeriods, ...this.existingPeriods];

    this.overlapResults = findOverlappingPeriods(candidate, allPeriods);
  };

  // --- Modal management ---

  openAddPeriodModal = () => {
    if (!this.canAddMorePeriods) return;
    this.editingPeriodId = null;
    this.editingPeriodGroup = this.args.sourceForm.pslfGroup || undefined;
    this.editingPeriodGroupError = [];
    this.useManualEntry = false;
    this.manualEmployerData = { ...DEFAULT_MANUAL_EMPLOYER_DATA };
    this.manualEmployerErrors = {};
    this.modalFormData = {
      employmentStartDate: '',
      employmentEndDate: '',
      employmentHours: 0,
    };
    this.modalFormErrors = {};
    this.overlapResults = [];
    this.isOpenPastPeriodModal = true;
    this.fetchExistingForms();
  };

  openEditPeriodModal = (period: PastEmploymentPeriod) => {
    this.editingPeriodId = period.id;
    this.editingPeriodGroupError = [];
    this.modalFormErrors = {};
    this.manualEmployerErrors = {};

    if (period.useManualEntry) {
      this.useManualEntry = true;
      this.editingPeriodGroup = undefined;
      this.manualEmployerData = {
        employerName: period.manualEmployerName || '',
        employerFein: period.manualEmployerFein || '',
        employerStreetAddress: period.manualEmployerStreetAddress || '',
        employerCity: period.manualEmployerCity || '',
        employerState: period.manualEmployerState || '',
        employerPostalCode: period.manualEmployerPostalCode || '',
      };
    } else {
      this.useManualEntry = false;
      this.editingPeriodGroup = period.pslfGroup;
      this.manualEmployerData = { ...DEFAULT_MANUAL_EMPLOYER_DATA };
    }

    this.modalFormData = {
      employmentStartDate: period.employmentStartDate,
      employmentEndDate: period.employmentEndDate,
      employmentHours: period.employmentHours,
    };
    this.overlapResults = [];
    this.isOpenPastPeriodModal = true;
    this.fetchExistingForms().then(() => this.checkForOverlaps());
  };

  closePastPeriodModal = () => {
    this.isOpenPastPeriodModal = false;
    this.editingPeriodId = null;
    this.editingPeriodGroup = undefined;
    this.editingPeriodGroupError = [];
    this.useManualEntry = false;
    this.manualEmployerData = { ...DEFAULT_MANUAL_EMPLOYER_DATA };
    this.manualEmployerErrors = {};
    this.overlapResults = [];
  };

  removePastPeriod = (periodId: string) => {
    this.args.onPeriodsChange(this.args.pastPeriods.filter((p) => p.id !== periodId));
  };

  // --- Employer selection ---

  selectModalGroup = (groupId: string | null) => {
    this.editingPeriodGroup = this.args.approverEmployee.company.pslfGroups.find(
      (group: PslfGroup) => group.id === groupId
    );
    this.editingPeriodGroupError = [];
  };

  toggleManualEntry = (checked: boolean) => {
    this.useManualEntry = checked;
    if (checked) {
      this.editingPeriodGroup = undefined;
      this.editingPeriodGroupError = [];
    } else {
      this.manualEmployerData = { ...DEFAULT_MANUAL_EMPLOYER_DATA };
      this.manualEmployerErrors = {};
    }
  };

  setManualEmployerField = (field: keyof ManualEmployerData, value: string) => {
    this.manualEmployerData = { ...this.manualEmployerData, [field]: value };
    if (Object.keys(this.manualEmployerErrors).length > 0) {
      this.validateManualEmployerFields();
    }
  };

  selectManualState = (stateId: string | null) => {
    this.manualEmployerData = { ...this.manualEmployerData, employerState: stateId || '' };
    if (Object.keys(this.manualEmployerErrors).length > 0) {
      this.validateManualEmployerFields();
    }
  };

  // --- Form fields ---

  updateModalField = (field: keyof ModalFormData, value: string) => {
    this.modalFormData = { ...this.modalFormData, [field]: value };
    if (Object.keys(this.modalFormErrors).length > 0) {
      this.validateModalForm();
    }
    if (field === 'employmentStartDate' || field === 'employmentEndDate') {
      this.checkForOverlaps();
    }
  };

  updateEmploymentHours = (value: string) => {
    this.modalFormData = { ...this.modalFormData, employmentHours: parseInt(value, 10) || 0 };
    if (Object.keys(this.modalFormErrors).length > 0) {
      this.validateModalForm();
    }
  };

  // --- Validation ---

  validateManualEmployerFields = (): boolean => {
    const result = v.safeParse(manualEmployerSchema, this.manualEmployerData);
    if (!result.success) {
      const errors: Record<string, string[]> = {};
      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          (errors[path] ??= []).push(issue.message);
        }
      }
      this.manualEmployerErrors = errors;
      return false;
    }
    this.manualEmployerErrors = {};
    return true;
  };

  validateModalForm = () => {
    if (this.useManualEntry) {
      this.editingPeriodGroupError = [];
    } else {
      if (!this.editingPeriodGroup) {
        this.editingPeriodGroupError = [this.intl.t('pslf.approver_obo.select_employer_group')];
      } else {
        this.editingPeriodGroupError = [];
      }
    }

    const result = v.safeParse(modalFormSchema, this.modalFormData);
    if (!result.success) {
      const errors: Record<string, string[]> = {};
      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          (errors[path] ??= []).push(issue.message);
        }
      }
      this.modalFormErrors = errors;
    } else {
      this.modalFormErrors = {};
    }

    let manualValid = true;
    if (this.useManualEntry) {
      manualValid = this.validateManualEmployerFields();
    }

    const formValid = Object.keys(this.modalFormErrors).length === 0;
    const employerValid = this.useManualEntry
      ? manualValid
      : this.editingPeriodGroupError.length === 0;

    return formValid && employerValid;
  };

  handleModalFormSubmit = () => {
    if (!this.validateModalForm()) return;
    if (!this.useManualEntry && !this.editingPeriodGroup) return;

    const period: PastEmploymentPeriod = {
      id: this.editingPeriodId || crypto.randomUUID(),
      employmentStartDate: this.modalFormData.employmentStartDate,
      employmentEndDate: this.modalFormData.employmentEndDate,
      employmentHours: this.modalFormData.employmentHours,
    };

    if (this.useManualEntry) {
      period.useManualEntry = true;
      period.manualEmployerName = this.manualEmployerData.employerName;
      period.manualEmployerFein = this.manualEmployerData.employerFein;
      period.manualEmployerStreetAddress = this.manualEmployerData.employerStreetAddress;
      period.manualEmployerCity = this.manualEmployerData.employerCity;
      period.manualEmployerState = this.manualEmployerData.employerState;
      period.manualEmployerPostalCode = this.manualEmployerData.employerPostalCode;
    } else {
      period.pslfGroup = this.editingPeriodGroup;
    }

    let updated: PastEmploymentPeriod[];
    if (this.isEditingExistingPeriod) {
      updated = this.args.pastPeriods.map((p) => (p.id === period.id ? period : p));
    } else {
      updated = [...this.args.pastPeriods, period];
    }

    this.args.onPeriodsChange(updated);
    this.closePastPeriodModal();
  };

  <template>
    <VStack class="gap-2">
      {{#each @pastPeriods as |period index|}}
        <Section class="border p-4 rounded-lg">
          <:header>
            {{#if period.useManualEntry}}
              {{period.manualEmployerName}}
              -
              {{period.manualEmployerFein}}
            {{else if period.pslfGroup}}
              {{period.pslfGroup.employerLegalName}}
              -
              {{period.pslfGroup.employerFein}}
            {{/if}}
          </:header>
          <:actions>
            <Button
              @intent="primary"
              @appearance="minimal"
              {{on "click" (fn this.openEditPeriodModal period)}}
              data-test="btn-edit-period-{{index}}"
              aria-label={{t "pslf.approver_obo.edit_period"}}
            >
              <PhPencil @size={{18}} />
            </Button>
            <Button
              @intent="danger"
              @appearance="minimal"
              {{on "click" (fn this.removePastPeriod period.id)}}
              data-test="btn-remove-period-{{index}}"
              aria-label={{t "common.remove"}}
            >
              <PhTrash @size={{18}} />
            </Button>
          </:actions>
          <:body>
            <VStack class="gap-2">
              <Text @variant="muted" @tag="span" class="text-sm">
                {{safeFormatDate
                  period.employmentStartDate
                  month="long"
                  day="2-digit"
                  year="numeric"
                  timeZone="UTC"
                }}
                -
                {{safeFormatDate
                  period.employmentEndDate
                  month="long"
                  day="2-digit"
                  year="numeric"
                  timeZone="UTC"
                }}
              </Text>
              <Text @variant="muted" @tag="span" class="text-sm">
                {{period.employmentHours}}
                {{t "pslf.start_new.hours_per_week"}}
              </Text>
            </VStack>
          </:body>
        </Section>
      {{/each}}

      {{#if this.canAddMorePeriods}}
        <Button
          @intent="primary"
          @appearance="minimal"
          @size="sm"
          {{on "click" this.openAddPeriodModal}}
          data-test="btn-add-period"
        >
          <PhPlus @size={{14}} class="mr-1" />
          {{t "pslf.approver_obo.add_period"}}
        </Button>
      {{else}}
        <Text @variant="muted" @tag="p" class="text-sm italic">
          {{t "pslf.approver_obo.max_periods_reached"}}
        </Text>
      {{/if}}
    </VStack>

    {{! ===== Past Period Modal ===== }}
    <Modal
      @isOpen={{this.isOpenPastPeriodModal}}
      @onClose={{this.closePastPeriodModal}}
      @closeOnOutsideClick={{false}}
      @size="xl"
    >
      <:header>
        {{if
          this.isEditingExistingPeriod
          (t "pslf.approver_obo.edit_period")
          (t "pslf.approver_obo.add_period")
        }}
      </:header>
      <:body>
        <Text @variant="muted" @tag="p" class="text-sm mb-4">
          {{t "pslf.approver_obo.past_periods_description" name=this.borrowerName}}
        </Text>
        <Form @data={{this.modalFormData}} @onSubmit={{this.handleModalFormSubmit}}>
          <VStack class="pt-0">
            <Section
              class="p-4 transition-colors
                {{if
                  this.useManualEntry
                  'opacity-60'
                  (if this.editingPeriodGroup 'border-purple-300 bg-purple-50' '')
                }}"
              data-test="modal-option-select-group"
            >
              <ReSelect
                @label={{t "pslf.approver_obo.employer_group"}}
                @items={{this.groupOptions}}
                @onChange={{this.selectModalGroup}}
                @selected={{if this.editingPeriodGroup this.editingPeriodGroup.id ""}}
                @disabled={{this.useManualEntry}}
                @errors={{this.editingPeriodGroupError}}
                @clearable={{false}}
                data-test="obo-modal-select-group"
              />
            </Section>
            <div class="flex items-center gap-3 my-1">
              <Divider class="flex-1" />
              <Text @variant="muted" class="text-sm font-semibold uppercase">
                {{t "or"}}
              </Text>
              <Divider class="flex-1" />
            </div>
            <Section
              class="p-4 transition-colors
                {{if
                  this.useManualEntry
                  'border-purple-300 bg-purple-50'
                  (if this.editingPeriodGroup 'opacity-60' '')
                }}"
              data-test="modal-option-manual-entry"
            >
              <VStack>
                <Checkbox
                  @label={{t "pslf.approver_obo.manual_entry_checkbox"}}
                  @checked={{this.useManualEntry}}
                  @onChange={{this.toggleManualEntry}}
                  data-test="obo-modal-checkbox-manual-entry"
                />
                {{#if this.useManualEntry}}
                  <VStack class="mt-2">
                    <Input
                      @name="manualEmployerName"
                      @label={{t "pslf.admin_review.employer_legal_name"}}
                      @value={{this.manualEmployerData.employerName}}
                      @onChange={{fn this.setManualEmployerField "employerName"}}
                      @isRequired={{true}}
                      @errors={{this.manualEmployerErrors.employerName}}
                      data-test="obo-modal-employer-name"
                    />
                    <Input
                      @name="manualEmployerFein"
                      @label={{t "pslf.admin_review.employer_fein"}}
                      @value={{this.manualEmployerData.employerFein}}
                      @onChange={{fn this.setManualEmployerField "employerFein"}}
                      @isRequired={{true}}
                      placeholder="XX-XXXXXXX"
                      {{inputmask mask="99-9999999"}}
                      @errors={{this.manualEmployerErrors.employerFein}}
                      data-test="obo-modal-employer-fein"
                    />
                    <Input
                      @name="manualEmployerStreetAddress"
                      @label={{t "pslf.admin_review.street_address"}}
                      @value={{this.manualEmployerData.employerStreetAddress}}
                      @onChange={{fn this.setManualEmployerField "employerStreetAddress"}}
                      @isRequired={{true}}
                      @errors={{this.manualEmployerErrors.employerStreetAddress}}
                      data-test="obo-modal-employer-street"
                    />
                    <HStack>
                      <Input
                        @name="manualEmployerCity"
                        @label={{t "pslf.admin_review.city"}}
                        @value={{this.manualEmployerData.employerCity}}
                        @onChange={{fn this.setManualEmployerField "employerCity"}}
                        @isRequired={{true}}
                        @errors={{this.manualEmployerErrors.employerCity}}
                        class="flex-1"
                        data-test="obo-modal-employer-city"
                      />
                      <ReSelect
                        @label={{t "pslf.admin_review.state"}}
                        @items={{this.usStateOptions}}
                        @selected={{this.manualEmployerData.employerState}}
                        @onChange={{this.selectManualState}}
                        @errors={{this.manualEmployerErrors.employerState}}
                        class="w-32"
                        data-test="obo-modal-employer-state"
                      />
                      <Input
                        @name="manualEmployerPostalCode"
                        @label={{t "pslf.admin_review.postal_code"}}
                        @value={{this.manualEmployerData.employerPostalCode}}
                        @onChange={{fn this.setManualEmployerField "employerPostalCode"}}
                        @isRequired={{true}}
                        placeholder="#####"
                        {{inputmask mask="99999"}}
                        @errors={{this.manualEmployerErrors.employerPostalCode}}
                        class="w-32"
                        data-test="obo-modal-employer-zip"
                      />
                    </HStack>
                  </VStack>
                {{/if}}
              </VStack>
            </Section>
            <Section>
              <VStack>
                <Input
                  @name="employmentStartDate"
                  @label={{t "pslf.workflow.start_date"}}
                  @type="date"
                  @value={{this.modalFormData.employmentStartDate}}
                  @onChange={{fn this.updateModalField "employmentStartDate"}}
                  @isRequired={{true}}
                  @errors={{this.modalFormErrors.employmentStartDate}}
                  max={{this.todayDateString}}
                  data-test="obo-modal-start-date"
                />
                <Input
                  @name="employmentEndDate"
                  @label={{t "pslf.workflow.end_date"}}
                  @type="date"
                  @value={{this.modalFormData.employmentEndDate}}
                  @onChange={{fn this.updateModalField "employmentEndDate"}}
                  @isRequired={{true}}
                  @errors={{this.modalFormErrors.employmentEndDate}}
                  min={{or this.modalFormData.employmentStartDate ""}}
                  max={{this.todayDateString}}
                  data-test="obo-modal-end-date"
                />
                <Input
                  @name="employmentHours"
                  @label={{t "pslf.employer_workflow.average_hours_per_week"}}
                  @type="number"
                  @value={{this.modalFormData.employmentHours}}
                  @onChange={{this.updateEmploymentHours}}
                  @isRequired={{true}}
                  @errors={{this.modalFormErrors.employmentHours}}
                  data-test="obo-modal-hours"
                />
              </VStack>
            </Section>
            {{#if this.overlapResults.length}}
              <AlertBanner @intent="warning" data-test="obo-modal-overlap-warning">
                <VStack class="gap-2 p-0">
                  <Text @variant="muted" @tag="p" @size="sm" @weight="semibold">
                    {{t "pslf.overlap_warning.description" count=this.overlapResults.length}}
                  </Text>
                  {{#each this.overlapResults as |overlap|}}
                    <div class="rounded border border-amber-300 bg-white px-3 py-2">
                      <div class="flex items-center">
                        <Text @size="sm">
                          {{or
                            overlap.period.employerName
                            (t "pslf.overlap_warning.unknown_employer")
                          }}
                        </Text>
                        <Text @variant="muted" @size="sm" class="ml-1">
                          {{safeFormatDate
                            overlap.period.startDate
                            month="short"
                            day="2-digit"
                            year="numeric"
                            timeZone="UTC"
                          }}
                          -
                          {{#if overlap.period.endDate}}
                            {{safeFormatDate
                              overlap.period.endDate
                              month="short"
                              day="2-digit"
                              year="numeric"
                              timeZone="UTC"
                            }}
                          {{else}}
                            {{t "pslf.overlap_warning.present"}}
                          {{/if}}
                        </Text>
                      </div>
                      {{#if overlap.period.status}}
                        <div class="flex items-center mt-1">
                          {{#if (and overlap.period.formId overlap.period.needsReview)}}
                            <LinkTo
                              @route="authenticated.admin.pslf.form.review"
                              @model={{overlap.period.formId}}
                              target="_blank"
                              class="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                              data-test="overlap-review-link"
                            >
                              <Text @variant="warning" @size="xs">
                                {{t "pslf.overlap_warning.needs_review"}}
                              </Text>
                              <PhArrowSquareOut @size={{14}} class="shrink-0" />
                            </LinkTo>
                          {{else}}
                            <Text @variant="muted" @size="xs">
                              {{t (concat "common.pslf.status." overlap.period.status)}}
                            </Text>
                          {{/if}}
                        </div>
                      {{/if}}
                    </div>
                  {{/each}}
                </VStack>
              </AlertBanner>
            {{/if}}
          </VStack>
          <HStack class="gap-2 justify-end mt-6">
            <Button
              @intent="default"
              @appearance="outlined"
              {{on "click" this.closePastPeriodModal}}
              data-test="obo-modal-btn-cancel"
            >
              {{t "common.cancel"}}
            </Button>
            <Button @intent="primary" @type="submit" data-test="obo-modal-btn-save">
              {{t "pslf.approver_obo.save_period"}}
            </Button>
          </HStack>
        </Form>
      </:body>
    </Modal>
  </template>
}
